import React from 'react';
import { getBlockStyle, getTextStyle, generateTextStyleClasses, generateTextStyleInlineStyles } from 'utils/blockStyleUtils';
import TooltipWrapper from 'components/shared/TooltipWrapper';

/**
 * LinksBlock - Shared component for displaying teacher links and videos
 *
 * Used in both edit mode (preview) and public mode (full display).
 * Shows videos with embeds and links with rich styling.
 *
 * Links are now language-agnostic and stored at profile level.
 *
 * @param {Object} props
 * @param {Array} props.links - Links array (language-agnostic)
 * @param {Object} props.translations - I18n translations object
 * @param {string} props.mode - Display mode: 'edit' (preview) or 'public' (full display)
 * @param {Object} props.pageDesign - Page design configuration for text styling
 */
export default function LinksBlock({
  links = [],
  translations,
  mode = 'public',
  pageDesign
}) {
  // Sort links by order
  const sortedLinks = [...links].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Get text styles
  const blockStyle = getBlockStyle(pageDesign, 'links');
  const titleTextStyle = getTextStyle(blockStyle, 'title');
  const contentTextStyle = getTextStyle(blockStyle, 'content');

  // Get tooltip text for links block (only shown in edit mode)
  const defaultTooltip = "We are not a traditional platform and don't worry about private contact with students. We want students to know you as fully as possible — feel free to add your Instagram, YouTube, LINE, Facebook, demo videos, or anything that helps students understand you.";
  const linksTooltipText = translations?.links_block_tooltip || defaultTooltip;

  // Render video icon (for preview mode)
  const renderVideoIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm12.553 1.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
    </svg>
  );

  // Render link icon (for preview mode)
  const renderLinkIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );

  // Edit mode: Show simple preview
  if (mode === 'edit') {
    return (
      <TooltipWrapper tooltip={linksTooltipText}>
        <div className={`links-block ${generateTextStyleClasses(contentTextStyle, pageDesign)}`} style={generateTextStyleInlineStyles(contentTextStyle)}>
        {sortedLinks.length > 0 ? (
          <div className="links-list flex flex-col gap-3">
            {sortedLinks.map((link, index) => (
              <div key={index} className="link-item flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="link-icon w-10 h-10 flex items-center justify-center bg-blue-50 rounded text-blue-600 flex-shrink-0">
                  {link.type === 'video' ? renderVideoIcon() : renderLinkIcon()}
                </div>
                <div className="link-content flex-1 min-w-0">
                  <p className="link-title mb-1 font-semibold">
                    {link.text || translations?.block_previews?.unnamed_link || '未命名連結'}
                  </p>
                  {link.url && (
                    <p className="link-url overflow-hidden text-ellipsis whitespace-nowrap">
                      {link.url}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-sm italic">
            {translations?.block_previews?.links_placeholder || '點擊編輯以新增連結或影片'}
          </p>
        )}
        </div>
      </TooltipWrapper>
    );
  }

  // Public mode: Show full display with video embeds
  return (
    <div className={`links-block space-y-6 ${generateTextStyleClasses(contentTextStyle, pageDesign)}`} style={generateTextStyleInlineStyles(contentTextStyle)}>
      {sortedLinks.map((link, index) => (
        <div key={link.id || index}>
          {link.type === 'video' && link.embed_url ? (
            // Video embed
            <div className="p-6">
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h3 className={`flex items-center gap-3 min-w-0 font-semibold ${generateTextStyleClasses(contentTextStyle, pageDesign)}`} style={generateTextStyleInlineStyles(contentTextStyle)}>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center flex-shrink-0 shadow-md">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    </div>
                    <span className="truncate">{link.text}</span>
                  </h3>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm flex-shrink-0 ${
                    link.platform === 'youtube' ? 'bg-red-100 text-red-700' :
                    link.platform === 'vimeo' ? 'bg-blue-100 text-blue-700' :
                    link.platform === 'bilibili' ? 'bg-pink-100 text-pink-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {link.platform === 'youtube' ? '▶ YouTube' :
                     link.platform === 'vimeo' ? '◉ Vimeo' :
                     link.platform === 'bilibili' ? '⚡ Bilibili' : link.platform}
                  </span>
                </div>
              </div>
              <div className="w-full">
                <div className="relative w-full rounded-xl overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={link.embed_url}
                    className="absolute top-0 left-0 w-full h-full border-0"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title={`${link.platform} Video: ${link.text}`}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          ) : (
            // Regular link
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block w-full bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2"
            >
              <div className="flex items-center justify-between gap-4">
                <span className={`flex-grow text-left font-semibold ${generateTextStyleClasses(contentTextStyle, pageDesign)}`} style={generateTextStyleInlineStyles(contentTextStyle)}>{link.text}</span>
                <svg
                  className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-all duration-200 flex-shrink-0 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
            </a>
          )}
        </div>
      ))}
    </div>
  );
}
